import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Search, TrendingUp, Clock, Music2, Users, ChevronRight } from 'lucide-react';
import { SongCard } from '../../components/song/SongCard.js';
import { ArtistCard } from '../../components/artist/ArtistCard.js';
import { songsService } from '../../services/songs.service.js';
import { genresService } from '../../services/genres.service.js';
import { artistsService } from '../../services/artists.service.js';

const POPULAR_TAGS = ['Pop', 'Rock', 'Reggaeton', 'K-Pop', 'Hip-Hop', 'Jazz', 'R&B', 'Electronica'];

function SectionSkeleton({
  count = 4,
  variant = 'vertical',
}: {
  count?: number;
  variant?: 'vertical' | 'horizontal';
}) {
  if (variant === 'horizontal') {
    return (
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-3 p-4 animate-pulse shrink-0">
            <div className="w-20 h-20 rounded-full bg-surface-hover" />
            <div className="w-16 h-3 rounded bg-surface-hover" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl overflow-hidden bg-surface animate-pulse">
          <div className="aspect-square bg-surface-hover" />
          <div className="p-3 space-y-2">
            <div className="h-4 bg-surface-hover rounded w-3/4" />
            <div className="h-3 bg-surface-hover rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function GenresSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="h-8 w-20 rounded-full bg-surface-hover animate-pulse" />
      ))}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  linkTo,
  linkText = 'Ver todos',
}: {
  icon: React.ElementType;
  title: string;
  linkTo: string;
  linkText?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
        <Icon size={22} className="text-primary" />
        {title}
      </h2>
      <Link
        to={linkTo}
        className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
      >
        {linkText}
        <ChevronRight size={16} />
      </Link>
    </div>
  );
}

export default function HomePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { data: trendingData, isLoading: trendingLoading } = useQuery({
    queryKey: ['songs', 'trending'],
    queryFn: () => songsService.getTrending(),
  });

  const { data: recentData, isLoading: recentLoading } = useQuery({
    queryKey: ['songs', 'recent'],
    queryFn: () => songsService.getRecent(),
  });

  const { data: genresData, isLoading: genresLoading } = useQuery({
    queryKey: ['genres'],
    queryFn: () => genresService.getGenres(),
  });

  const { data: featuredArtistsData, isLoading: artistsLoading } = useQuery({
    queryKey: ['artists', 'featured'],
    queryFn: () => artistsService.getFeatured(),
  });

  const trending = trendingData?.data ?? [];
  const recent = recentData?.data ?? [];
  const genres = genresData?.data ?? [];
  const featuredArtists = featuredArtistsData?.data ?? [];

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  }

  return (
    <>
      <Helmet>
        <title>LyricsHub - Encuentra la letra de tu cancion favorita</title>
        <meta
          name="description"
          content="Descubre letras de canciones, artistas y albums. Busca entre miles de letras en espanol, ingles y mas idiomas."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-8 md:p-12 mb-8">
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
            Encuentra la letra de tu cancion favorita
          </h1>
          <p className="text-text-secondary mb-6">
            Miles de letras en espanol, ingles y mas idiomas
          </p>
          <form onSubmit={handleSearch} className="relative max-w-lg mx-auto">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar canciones, artistas, albums..."
              className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-base"
            />
          </form>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {POPULAR_TAGS.map((tag) => (
              <Link
                key={tag}
                to={`/genres/${tag.toLowerCase()}`}
                className="px-3 py-1 text-sm rounded-full bg-surface/80 text-text-secondary hover:bg-primary/10 hover:text-primary transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Section */}
      <section className="mb-8">
        <SectionHeader icon={TrendingUp} title="Trending Ahora" linkTo="/charts/weekly" />
        {trendingLoading ? (
          <SectionSkeleton count={6} />
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
            {trending.slice(0, 8).map((song: any) => (
              <div key={song.id} className="w-44 shrink-0">
                <SongCard
                  slug={song.slug}
                  title={song.title}
                  artistName={song.artistName}
                  artistSlug={song.artistSlug}
                  coverUrl={song.coverUrl}
                  genreName={song.genreName}
                  viewCount={song.viewCount}
                  variant="vertical"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent Section */}
      <section className="mb-8">
        <SectionHeader icon={Clock} title="Recien Agregadas" linkTo="/search?sort=recent" />
        {recentLoading ? (
          <SectionSkeleton count={4} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recent.slice(0, 8).map((song: any) => (
              <SongCard
                key={song.id}
                slug={song.slug}
                title={song.title}
                artistName={song.artistName}
                artistSlug={song.artistSlug}
                coverUrl={song.coverUrl}
                genreName={song.genreName}
                viewCount={song.viewCount}
                variant="vertical"
              />
            ))}
          </div>
        )}
      </section>

      {/* Genres Section */}
      <section className="mb-8">
        <SectionHeader icon={Music2} title="Generos" linkTo="/genres" />
        {genresLoading ? (
          <GenresSkeleton />
        ) : (
          <div className="flex flex-wrap gap-2">
            {genres.map((genre: any) => (
              <Link
                key={genre.id}
                to={`/genres/${genre.slug}`}
                className="px-4 py-2 rounded-full bg-surface text-sm font-medium text-text-secondary hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/30 transition-all"
              >
                {genre.name}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Featured Artists Section */}
      <section className="mb-8">
        <SectionHeader icon={Users} title="Artistas Destacados" linkTo="/search?tab=artists" />
        {artistsLoading ? (
          <SectionSkeleton count={6} variant="horizontal" />
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {featuredArtists.slice(0, 8).map((artist: any) => (
              <div key={artist.id} className="shrink-0">
                <ArtistCard
                  slug={artist.slug}
                  name={artist.name}
                  avatarUrl={artist.avatarUrl}
                  isVerified={artist.isVerified}
                  songCount={artist.songCount}
                />
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
